import { useEffect, useRef, useState } from 'react';

/**
 * ↑/↓ (wrapping), Enter and Esc for a launch list of `rows`, the active row
 * (reset to the first with `resetActive`, e.g. on typing) scrolled into view,
 * and `rowProps()` — call it once per rendered row, in `rows` order.
 */
export function useLaunchListNav(rows, { onPick, onClose }) {
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const clampActive = Math.min(active, Math.max(0, rows.length - 1));

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [clampActive]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = rows.length;
      if (!n) return;
      setActive((i) => (Math.min(i, n - 1) + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onPick(rows[clampActive], e);
    }
  };

  let idx = -1;
  const rowProps = () => {
    idx += 1;
    const i = idx;
    return {
      'data-active': i === clampActive ? 'true' : undefined,
      className: `term-menu-row${i === clampActive ? ' term-menu-row--active' : ''}`,
      onMouseEnter: () => setActive(i),
      role: 'menuitem',
    };
  };

  return { listRef, onKeyDown, rowProps, resetActive: () => setActive(0) };
}
